import { Table as AntTable, Tooltip } from 'antd';
import { ReactNode, useMemo } from 'react';
import { ColumnsType, TablePaginationConfig, TableProps } from 'antd/es/table';
import { InfoCircleOutlined } from '@ant-design/icons';

import Styles from './styles';
import { GetRowKey, TableLocale, TableRowSelection } from 'antd/es/table/interface';
const { Table: StyledTable, TableHeader, TableHead, ColumnName, CellLabel } = Styles;

export type TableColumnDefinition<T> = {
  dataIndex?: string;
  width?: string;
  title?: string | ReactNode;
  tooltip?: string;
  key: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onCell?: (data: T, index?: number) => React.HTMLAttributes<any> & React.TdHTMLAttributes<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  render?: (value: any, record: T, index: number) => ReactNode;
  sorter?: (a: T, b: T) => number;
  textWrap?: string;
  ellipsis?: boolean;
};

interface Props<T extends object> {
  data: T[];
  loading?: boolean;
  locale?: TableLocale | undefined;
  pagination?: false | TablePaginationConfig;
  rowSelection?: TableRowSelection;
  columns: TableColumnDefinition<T>[];
  props?: TableProps<T>;
  rowKey?: string | keyof T | GetRowKey<T>;
}

export function Table<T extends object>(props: Props<T>) {
  const columns: ColumnsType<T> = useMemo(() => {
    return props.columns.map(column => {
      return {
        title: column.title ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <ColumnName>{column.title}</ColumnName>
            {!!column.tooltip && (
              <Tooltip title={column.tooltip} placement="bottomLeft">
                <InfoCircleOutlined />
              </Tooltip>
            )}
          </div>
        ) : null,
        width: column.width,
        dataIndex: column.dataIndex,
        textWrap: column.textWrap,
        ellipsis: column.ellipsis,
        render: (value, record, index) => {
          if (column.render) return column.render(value, record, index);
          return <CellLabel width={column.width}>{value}</CellLabel>;
        },
        showSorterTooltip: column.sorter ? { target: 'full-header' } : false,
        sorter: column.sorter,
      };
    });
  }, [props.columns]);

  const otherProps = props.props || {};

  return (
    <AntTable
      dataSource={props.data}
      loading={props.loading}
      columns={columns}
      pagination={props.pagination}
      locale={props.locale}
      rowSelection={props.rowSelection}
      rowKey={props.rowKey}
      components={{
        table: StyledTable,
        header: {
          wrapper: TableHead,
          cell: TableHeader,
        },
      }}
      {...otherProps}
    />
  );
}
